import { announce } from "../lib/announce.ts";
import { Component } from "../lib/component.ts";
import { claimInert, releaseInert } from "../lib/inert-stack.ts";
import { resampleToolbar } from "../lib/resample-toolbar.ts";
import { ScrollIntent } from "../lib/scroll-intent.ts";
import { lockScroll, unlockScroll } from "../lib/scroll-lock.ts";

const LOCK_ID = "hero-gallery";
const SWIPE_THRESHOLD = 50;
const TRANSITION_TIMEOUT_MS = 600;
const POST_TRANSITION_COOLDOWN_MS = 80;

interface HeroGalleryRefs {
  slides?: HTMLElement[];

  desktopMedia?: HTMLElement[];

  mobileMedia?: HTMLElement[];
}

interface CaptureEvent {
  t: number;
  dy: number;
  dm: number;
}

class HeroGallery extends Component<HeroGalleryRefs> {
  #currentIndex = 0;

  #transitioning = false;

  #transitionGen = 0;

  #transitionTimer: number | undefined;

  #touchStartY: number | null = null;

  #done = false;

  #queuedDirection = 0;

  #debug =
    typeof location !== "undefined" && new URLSearchParams(location.search).has("debug-hero");

  #captureEnabled =
    typeof location !== "undefined" && new URLSearchParams(location.search).has("capture-hero");

  #capture: CaptureEvent[] = [];

  #intent = new ScrollIntent({
    onLog: (classification, ctx) => {
      if (!this.#debug) return;
      const fmt = (n: number): string => n.toFixed(1);
      const pending = ctx.pendingAbsDelta != null ? ` pending=${fmt(ctx.pendingAbsDelta)}` : "";
      const t = this.#transitioning ? " (transitioning)" : "";
      console.log(
        `[hero] ${classification.padEnd(13)} dt=${fmt(ctx.dt)} |Δ|=${fmt(ctx.absDelta)} dir=${ctx.dir} mode=${ctx.deltaMode} peak=${fmt(ctx.peak)} tailMax=${fmt(ctx.tailMax)} tail=[${ctx.tail.map(fmt).join(",")}]${pending}${t}`,
      );
    },
  });

  #activeController: AbortController | null = null;

  #mobileQuery = window.matchMedia("(max-aspect-ratio: 1/1)");

  #skipLink: HTMLButtonElement | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    if (window.Shopify?.designMode) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const slides = this.refs.slides;
    if (!Array.isArray(slides) || slides.length === 0) return;

    this.classList.add("hero-gallery--active");
    this.#initSkipLink();
    this.#initSlides(slides);
    this.#initMedia(slides);

    requestAnimationFrame(() => {
      if (this.signal.aborted) return;
      if (window.scrollY > 0) {
        this.#currentIndex = slides.length;
        this.#syncSlides({ animate: false });
        this.#disengage();
      } else {
        this.#engage();
      }
    });

    window.addEventListener("resize", this.#updateSkipLinkPosition, {
      signal: this.signal,
    });
    this.#mobileQuery.addEventListener("change", this.#onViewportChange, {
      signal: this.signal,
    });
    this.addEventListener("transitionend", this.#onTransitionEnd, {
      signal: this.signal,
    });
  }

  disconnectedCallback(): void {
    clearTimeout(this.#transitionTimer);
    this.#activeController?.abort();
    releaseInert(LOCK_ID);
    unlockScroll(LOCK_ID);
    super.disconnectedCallback();
  }

  #initSkipLink(): void {
    const label = this.dataset.skipLabel;
    if (!label) return;
    const link = document.createElement("button");
    link.type = "button";
    link.className = "hero-gallery__skip";
    link.textContent = label;
    link.addEventListener(
      "click",
      () => {
        this.#skipToEnd();
      },
      { signal: this.signal },
    );
    this.#skipLink = link;
    this.#updateSkipLinkPosition();
  }

  #updateSkipLinkPosition = (): void => {
    if (!this.#skipLink) return;
    const header = document.querySelector("site-header");
    if (header instanceof HTMLElement) {
      this.#skipLink.style.top = `${header.getBoundingClientRect().bottom}px`;
    }
  };

  #initSlides(slides: HTMLElement[]): void {
    const count = slides.length;
    for (let i = 0; i < count; i++) {
      slides[i].style.zIndex = String(count - i);
      slides[i].tabIndex = -1;
    }
    if (this.#skipLink) this.#skipLink.style.zIndex = String(count + 1);
    this.#syncSlides({ animate: false, focus: false });
  }

  /**
   * Reconcile every slide's DOM state to match `#currentIndex`.
   *
   * - Slides before current: translated off-screen, inert
   * - Current slide: no transform, not inert, focused
   * - Slides after current: no transform (occluded by z-index), inert
   * - Past the end (done): all slides translated off-screen, none inert
   */
  #syncSlides({ animate = true, focus = true }: { animate?: boolean; focus?: boolean } = {}): void {
    const slides = this.refs.slides;
    if (!Array.isArray(slides)) return;
    const pastEnd = this.#currentIndex >= slides.length;

    if (!animate) {
      for (const slide of slides) {
        slide.style.transition = "none";
      }
    }

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (pastEnd) {
        slide.style.transform = "translateY(-100%)";
        slide.inert = false;
        if (!animate) slide.style.visibility = "hidden";
      } else if (i < this.#currentIndex) {
        slide.style.transform = "translateY(-100%)";
        slide.inert = true;
        if (!animate) slide.style.visibility = "hidden";
      } else if (i === this.#currentIndex) {
        slide.style.transform = "";
        slide.style.visibility = "";
        slide.inert = false;
      } else {
        slide.style.transform = "";
        slide.style.visibility = "";
        slide.inert = true;
      }
    }

    if (!animate) {
      slides[0]?.getBoundingClientRect();
      for (const slide of slides) {
        slide.style.transition = "";
      }
    }

    if (focus && !pastEnd && this.#currentIndex < slides.length) {
      slides[this.#currentIndex].focus({ preventScroll: true });
    }
  }

  #getMediaContainers(slide: HTMLElement): HTMLElement[] {
    const containers: HTMLElement[] = [];
    for (const refs of [this.refs.desktopMedia, this.refs.mobileMedia]) {
      const match = refs?.find((el) => slide.contains(el));
      if (match) containers.push(match);
    }
    if (containers.length > 0) return containers;
    const first = slide.firstElementChild;
    return first instanceof HTMLElement ? [first] : [];
  }

  #initMedia(slides: HTMLElement[]): void {
    for (let i = 0; i < slides.length; i++) {
      const containers = this.#getMediaContainers(slides[i]);

      for (const c of containers) {
        for (const img of c.querySelectorAll("img")) this.#revealOnLoad(img);
      }

      if (i === 0) {
        for (const c of containers) {
          for (const img of c.querySelectorAll("img")) img.loading = "eager";
        }
        const activeVideo = this.#getActiveVideo(slides[i]);
        for (const c of containers) {
          const video = c.querySelector("video");
          if (video && video !== activeVideo) {
            video.removeAttribute("autoplay");
            video.pause();
          }
        }
      } else {
        for (const c of containers) {
          const video = c.querySelector("video");
          if (video) {
            video.removeAttribute("autoplay");
            video.pause();
          }
        }
      }
    }

    this.#preloadNext(0);
  }

  #preloadNext(index: number): void {
    const slides = this.refs.slides;
    if (!Array.isArray(slides)) return;
    const next = slides[index + 1];
    if (!next) return;

    for (const c of this.#getMediaContainers(next)) {
      for (const img of c.querySelectorAll("img")) img.loading = "eager";
    }

    const video = this.#getActiveVideo(next);
    if (video) video.preload = "auto";
  }

  #engage(): void {
    this.#done = false;
    this.#queuedDirection = 0;
    this.#intent.reset();
    if (this.#captureEnabled) this.#installCapture();
    this.#activeController?.abort();
    this.#activeController = new AbortController();
    const { signal } = this.#activeController;

    this.style.pointerEvents = "";
    this.style.visibility = "";
    resampleToolbar({ top: "black" });
    lockScroll(LOCK_ID);
    claimInert(LOCK_ID, this, { skip: "site-header", priority: -1 });
    if (this.#skipLink) this.prepend(this.#skipLink);

    window.addEventListener("wheel", this.#onWheel, { passive: false, signal });
    window.addEventListener("keydown", this.#onEscape, { signal });
    window.addEventListener("keydown", this.#onKeyDown, { signal });
    this.addEventListener("touchstart", this.#onTouchStart, { passive: true, signal });
    this.addEventListener("touchmove", this.#onTouchMove, { passive: false, signal });
    this.addEventListener("touchend", this.#onTouchEnd, { passive: true, signal });
    document.addEventListener("close", this.#onOverlayClose, { signal });
  }

  #disengage(): void {
    this.#done = true;
    this.#activeController?.abort();
    this.#activeController = null;

    unlockScroll(LOCK_ID);
    this.style.pointerEvents = "none";
    this.style.visibility = "hidden";
    releaseInert(LOCK_ID);
    this.#skipLink?.remove();

    resampleToolbar({ top: "var(--background-color)" });

    window.addEventListener("wheel", this.#onWheelWhileDone, {
      passive: true,
      signal: this.signal,
    });
    window.addEventListener("touchstart", this.#onTouchStartWhileDone, {
      passive: true,
      signal: this.signal,
    });
    window.addEventListener("touchmove", this.#onTouchMoveWhileDone, {
      passive: false,
      signal: this.signal,
    });
    window.addEventListener("keydown", this.#onKeyDownWhileDone, {
      signal: this.signal,
    });
  }

  #shouldIgnoreInput(): boolean {
    return this.closest("[inert]") !== null;
  }

  #onWheel = (e: WheelEvent): void => {
    if (this.#shouldIgnoreInput()) return;
    e.preventDefault();

    const now = performance.now();
    if (this.#captureEnabled) this.#capture.push({ t: now, dy: e.deltaY, dm: e.deltaMode });

    const result = this.#intent.process({
      timestamp: now,
      deltaY: e.deltaY,
      deltaMode: e.deltaMode,
    });

    if (result.fire) this.#fireOrQueue(result.fire);
  };

  #installCapture(): void {
    if (typeof window === "undefined") return;
    if (Reflect.get(window, "__heroCaptureDump")) return;
    Reflect.set(window, "__heroCaptureDump", (label: string): unknown => {
      const events = this.#capture;
      const first = events[0]?.t ?? 0;
      const payload = {
        version: 1,
        label: label ?? "unlabeled",
        events: events.map((e) => ({
          t: +(e.t - first).toFixed(1),
          dy: e.dy,
          dm: e.dm,
        })),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hero-capture-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      this.#capture.length = 0;
      return payload;
    });
    Reflect.set(window, "__heroCaptureClear", (): void => {
      this.#capture.length = 0;
    });
    console.log(
      "[hero] capture mode: __heroCaptureDump('label') downloads and clears events; __heroCaptureClear() discards them",
    );
  }

  #fireOrQueue(dir: number): void {
    const slides = this.refs.slides;
    const atStart = this.#currentIndex <= 0;
    const atEnd = Array.isArray(slides) && this.#currentIndex >= slides.length;
    if ((dir < 0 && atStart) || (dir > 0 && atEnd)) {
      if (this.#debug)
        console.log(
          `[hero] fire-noop dir=${dir} index=${this.#currentIndex} atStart=${atStart} atEnd=${atEnd}`,
        );
      return;
    }
    if (this.#transitioning) {
      this.#queuedDirection = dir;
      if (this.#debug) console.log(`[hero] queued dir=${dir}`);
      return;
    }
    if (dir > 0) this.#advance();
    else if (dir < 0) this.#retreat();
  }

  #onWheelWhileDone = (e: WheelEvent): void => {
    if (!this.#done) return;
    if (this.#shouldIgnoreInput()) return;
    if (window.scrollY === 0 && e.deltaY < 0) {
      this.#reengageLast();
    }
  };

  #onTouchStartWhileDone = (e: TouchEvent): void => {
    if (!this.#done) return;
    if (this.#shouldIgnoreInput()) return;
    this.#touchStartY = window.scrollY <= 0 ? e.touches[0].clientY : null;
  };

  #onTouchMoveWhileDone = (e: TouchEvent): void => {
    if (!this.#done || this.#touchStartY === null) return;
    if (this.#shouldIgnoreInput()) return;
    if (window.scrollY > 0) return;

    const deltaY = e.touches[0].clientY - this.#touchStartY;
    if (deltaY > 0) {
      e.preventDefault();
      if (deltaY > 10) {
        this.#touchStartY = null;
        this.#reengageLast();
      }
    }
  };

  #onKeyDownWhileDone = (e: KeyboardEvent): void => {
    if (!this.#done) return;
    if (this.#shouldIgnoreInput()) return;
    if (window.scrollY > 0) return;
    if (e.target instanceof HTMLElement && e.target.closest("a, button, input, select, textarea"))
      return;
    if (e.key === "ArrowUp" || e.key === "PageUp") {
      e.preventDefault();
      this.#reengageLast();
    }
  };

  #onOverlayClose = (): void => {
    this.style.position = "absolute";
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.style.removeProperty("position");
      });
    });
  };

  #onEscape = (e: KeyboardEvent): void => {
    if (e.key !== "Escape" || e.defaultPrevented) return;
    if (this.#shouldIgnoreInput()) return;
    e.preventDefault();
    this.#skipToEnd();
  };

  #onKeyDown = (e: KeyboardEvent): void => {
    if (this.#transitioning) return;
    if (this.#shouldIgnoreInput()) return;
    if (e.target instanceof HTMLElement && e.target.closest("a, button, input, select, textarea"))
      return;

    if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") {
      e.preventDefault();
      this.#advance();
    } else if (e.key === "ArrowUp" || e.key === "PageUp") {
      e.preventDefault();
      this.#retreat();
    }
  };

  #onTouchStart = (e: TouchEvent): void => {
    this.#touchStartY = e.touches[0].clientY;
  };

  #onTouchMove = (e: TouchEvent): void => {
    if (this.#transitioning) return;
    if (this.#touchStartY === null) return;

    e.preventDefault();
    const deltaY = this.#touchStartY - e.touches[0].clientY;

    if (Math.abs(deltaY) >= SWIPE_THRESHOLD) {
      this.#touchStartY = null;
      if (deltaY > 0) {
        this.#advance();
      } else {
        this.#retreat();
      }
    }
  };

  #onTouchEnd = (): void => {
    this.#touchStartY = null;
  };

  #advance(): void {
    const slides = this.refs.slides;
    if (!Array.isArray(slides)) return;
    if (this.#currentIndex >= slides.length) return;

    const prev = slides[this.#currentIndex];
    this.#pauseMedia(prev);
    this.#currentIndex++;
    this.#syncSlides();
    this.#startTransition();
    document.dispatchEvent(new Event("scroll"));

    if (this.#currentIndex < slides.length) {
      this.#playMedia(slides[this.#currentIndex]);
      this.#preloadNext(this.#currentIndex);
      this.#announceSlide();
    }
  }

  #retreat(): void {
    if (this.#currentIndex <= 0) return;
    const slides = this.refs.slides;
    if (!Array.isArray(slides)) return;

    if (this.#currentIndex < slides.length) {
      this.#pauseMedia(slides[this.#currentIndex]);
    }
    this.#currentIndex--;
    this.#syncSlides();
    this.#startTransition();
    document.dispatchEvent(new Event("scroll"));

    this.#playMedia(slides[this.#currentIndex]);
    this.#announceSlide();
  }

  #skipToEnd(): void {
    const slides = this.refs.slides;
    if (!Array.isArray(slides)) return;

    for (let i = this.#currentIndex; i < slides.length; i++) {
      this.#pauseMedia(slides[i]);
    }

    this.#currentIndex = slides.length;
    this.#syncSlides({ animate: false });
    this.#disengage();
    this.#focusPastGallery();
    document.dispatchEvent(new Event("scroll"));
  }

  #focusPastGallery(): void {
    const target = document.createElement("span");
    target.className = "visually-hidden";
    target.style.outline = "none";
    target.tabIndex = -1;
    this.after(target);
    target.focus();
    target.addEventListener("blur", () => target.remove(), { once: true });
  }

  #announceSlide(): void {
    const template = this.dataset.slideAnnouncement;
    const slides = this.refs.slides;
    if (!template || !Array.isArray(slides)) return;

    const message = template
      .replace("{{ current }}", String(this.#currentIndex + 1))
      .replace("{{ total }}", String(slides.length));
    announce(message);
  }

  #reengageLast(): void {
    const slides = this.refs.slides;
    if (!Array.isArray(slides) || slides.length === 0) return;

    this.#currentIndex = slides.length;
    this.#syncSlides({ animate: false });

    this.#currentIndex = slides.length - 1;
    this.#syncSlides();
    this.#startTransition();

    this.#engage();

    // The scroll that brought us back is likely still emitting wheel/touch
    // events (momentum). Seed the gesture tracker so those events absorb
    // as continuation of the re-engagement rather than triggering a retreat.
    this.#intent.seed(-1, performance.now());

    this.#playMedia(slides[this.#currentIndex]);

    // Scroll is locked so no scroll events fire — notify the header
    // to re-evaluate its color against the now-visible slide.
    document.dispatchEvent(new Event("scroll"));
  }

  #startTransition(): void {
    this.#transitioning = true;
    const gen = ++this.#transitionGen;
    clearTimeout(this.#transitionTimer);
    this.#transitionTimer = window.setTimeout(() => {
      if (this.#transitionGen === gen) this.#endTransition();
    }, TRANSITION_TIMEOUT_MS);
  }

  #endTransition(): void {
    clearTimeout(this.#transitionTimer);
    this.#transitioning = false;
    document.dispatchEvent(new Event("scroll"));

    const queued = this.#queuedDirection;
    this.#queuedDirection = 0;
    if (queued > 0) {
      this.#advance();
      return;
    }
    if (queued < 0) {
      this.#retreat();
      return;
    }

    const slides = this.refs.slides;
    if (!this.#done && Array.isArray(slides) && this.#currentIndex >= slides.length) {
      this.#disengage();
    }
  }

  #onTransitionEnd = (e: TransitionEvent): void => {
    if (e.propertyName !== "transform") return;
    if (!(e.target instanceof HTMLElement)) return;
    const target = e.target;
    if (!target.classList.contains("hero-gallery-slide")) return;
    if (target.style.transform) target.style.visibility = "hidden";
    const gen = this.#transitionGen;
    clearTimeout(this.#transitionTimer);
    this.#transitionTimer = window.setTimeout(() => {
      if (this.#transitionGen === gen) this.#endTransition();
    }, POST_TRANSITION_COOLDOWN_MS);
  };

  #revealOnLoad(img: HTMLImageElement): void {
    const reveal = (): void => {
      img.style.filter = "opacity(1)";
    };
    if (img.complete) {
      reveal();
    } else {
      img.addEventListener("load", reveal, { once: true, signal: this.signal });
    }
  }

  #getActiveVideo(slide: HTMLElement): HTMLVideoElement | null {
    const refs = this.#mobileQuery.matches ? this.refs.mobileMedia : this.refs.desktopMedia;
    const container = refs?.find((el) => slide.contains(el));
    if (container) return container.querySelector("video");
    return slide.querySelector("video");
  }

  #onViewportChange = (): void => {
    if (this.#done) return;
    const slides = this.refs.slides;
    if (!Array.isArray(slides)) return;
    if (this.#currentIndex >= slides.length) return;

    const slide = slides[this.#currentIndex];
    this.#pauseMedia(slide);
    const active = this.#getActiveVideo(slide);
    if (active) active.play();
  };

  #playMedia(slide: HTMLElement): void {
    const video = this.#getActiveVideo(slide);
    if (video) video.play();
  }

  #pauseMedia(slide: HTMLElement): void {
    for (const c of this.#getMediaContainers(slide)) {
      const video = c.querySelector("video");
      if (video) video.pause();
    }
  }
}

if (!customElements.get("hero-gallery")) {
  customElements.define("hero-gallery", HeroGallery);
}
